import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import { ActivityAPI } from "../api";
import AdminIcon from "./adminIcons";
import { ADMIN_BUTTON_SX, ADMIN_COLORS, ADMIN_FONT, ADMIN_INTER_FONT } from "./adminUi";
import { findJoineeForm } from "./joineeStatus";
import {
  activityActionParts,
  activityActor,
  activityTypeMeta,
  formatActivityTime,
  groupActivitiesByDay,
} from "./activityTypes";

var PAGE_SIZE = 30;
var TOAST_OPTS = { hideProgressBar: true, closeOnClick: true };

var TEXT = {
  day: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: 600, lineHeight: "20px", color: ADMIN_COLORS.labelSecondary },
  name: { fontFamily: ADMIN_INTER_FONT, fontSize: 17, fontWeight: 800, lineHeight: "24px", color: ADMIN_COLORS.label },
  role: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: 500, lineHeight: "22px", color: ADMIN_COLORS.labelSecondary },
  time: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: 500, lineHeight: "22px", color: ADMIN_COLORS.labelSecondary },
  action: { fontFamily: ADMIN_FONT, fontSize: 15, fontWeight: 400, lineHeight: "22px", color: ADMIN_COLORS.label },
  comment: { fontFamily: ADMIN_FONT, fontSize: 15, fontWeight: 400, lineHeight: "22px", color: ADMIN_COLORS.label },
  detail: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: 400, lineHeight: "20px", color: ADMIN_COLORS.labelSecondary },
};

var FORM_NAME_SX = {
  fontFamily: ADMIN_FONT,
  fontSize: "inherit",
  lineHeight: "inherit",
  fontWeight: 600,
  color: ADMIN_COLORS.brand,
  verticalAlign: "baseline",
};

function FormName(props) {
  var form = props.form;
  if (!props.onOpen || !findJoineeForm(form.key)) {
    return (
      <Box component="span" sx={FORM_NAME_SX}>
        {form.label}
      </Box>
    );
  }
  return (
    <ButtonBase
      component="span"
      role="link"
      onClick={function () {
        props.onOpen(form.key);
      }}
      sx={Object.assign({}, FORM_NAME_SX, {
        userSelect: "text",
        borderRadius: "2px",
        "&:hover": { textDecoration: "underline" },
        "&.Mui-focusVisible": { outline: "2px solid " + ADMIN_COLORS.brand, outlineOffset: "2px" },
      })}
    >
      {form.label}
    </ButtonBase>
  );
}

function ActivityItem(props) {
  var activity = props.activity;
  var meta = activityTypeMeta(activity.type);
  var actor = activityActor(activity);
  var action = activityActionParts(activity);

  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        gap: "12px",
        py: "12px",
        borderBottom: props.last ? "none" : "1px solid " + ADMIN_COLORS.lineSoft,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: 32,
          height: 32,
          flexShrink: 0,
          borderRadius: "50%",
          bgcolor: meta.tone.bg,
          color: meta.tone.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <AdminIcon name={meta.icon} size={18} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "12px" }}>
          <Typography sx={Object.assign({ minWidth: 0, wordBreak: "break-word" }, TEXT.name)}>
            {actor.name}
            {actor.role ? (
              <Box component="span" sx={TEXT.role}>
                {" · " + actor.role}
              </Box>
            ) : null}
          </Typography>
          <Typography component="time" dateTime={activity.createdAt} sx={Object.assign({ flexShrink: 0, whiteSpace: "nowrap" }, TEXT.time)}>
            {formatActivityTime(activity.createdAt)}
          </Typography>
        </Box>

        <Typography sx={TEXT.action}>
          {action.prefix}
          {action.form ? (
            <>
              {" "}
              <FormName form={action.form} onOpen={props.onOpenForm} />
            </>
          ) : null}
          {action.suffix ? " " + action.suffix : null}
        </Typography>

        {activity.comment ? (
          <Typography
            sx={Object.assign(
              {
                mt: "4px",
                px: "12px",
                py: "10px",
                bgcolor: "#F7F8F9",
                border: "1px solid " + ADMIN_COLORS.lineSoft,
                borderRadius: "8px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              },
              TEXT.comment
            )}
          >
            {activity.comment}
          </Typography>
        ) : meta.detail ? (
          <Typography sx={TEXT.detail}>{meta.detail}</Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function FeedMessage(props) {
  return (
    <Box sx={{ py: "32px", display: "flex", flexDirection: "column", alignItems: "center", gap: "12px", textAlign: "center" }}>
      <Typography sx={Object.assign({}, TEXT.action, { color: ADMIN_COLORS.labelSecondary })}>{props.children}</Typography>
      {props.action || null}
    </Box>
  );
}

/**
 * Candidate onboarding audit trail, newest first.
 * Reloads when `refreshKey` changes; `onOpenForm(formId)` makes form names clickable.
 */
function ActivityFeed(props) {
  var candidateId = props.candidateId;
  var [feed, setFeed] = useState({ candidateId: null, items: [], hasMore: false, status: "loading" });
  var [loadingMore, setLoadingMore] = useState(false);
  var [reloadKey, setReloadKey] = useState(0);

  useEffect(
    function () {
      if (!candidateId) return undefined;
      var active = true;
      setFeed(function (prev) {
        return Object.assign({}, prev, { status: prev.candidateId === candidateId ? "refreshing" : "loading" });
      });
      ActivityAPI.list(candidateId, { limit: PAGE_SIZE })
        .then(function (res) {
          if (!active) return;
          if (!res || !res.success || !res.data) throw new Error("failed");
          setFeed({ candidateId: candidateId, items: res.data.items || [], hasMore: Boolean(res.data.hasMore), status: "ready" });
        })
        .catch(function (err) {
          console.log(err);
          if (!active) return;
          setFeed(function (prev) {
            return Object.assign({}, prev, { status: "error" });
          });
        });
      return function () {
        active = false;
      };
    },
    [candidateId, props.refreshKey, reloadKey]
  );

  function reload() {
    setReloadKey(function (k) {
      return k + 1;
    });
  }

  function loadOlder() {
    var last = feed.items[feed.items.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    ActivityAPI.list(candidateId, { limit: PAGE_SIZE, before: last.createdAt })
      .then(function (res) {
        if (!res || !res.success || !res.data) throw new Error("failed");
        setFeed(function (prev) {
          if (prev.candidateId !== candidateId) return prev;
          var seen = {};
          prev.items.forEach(function (item) {
            seen[item._id] = true;
          });
          var older = (res.data.items || []).filter(function (item) {
            return !seen[item._id];
          });
          return Object.assign({}, prev, { items: prev.items.concat(older), hasMore: Boolean(res.data.hasMore) });
        });
      })
      .catch(function (err) {
        console.log(err);
        toast.error("Unable to load older activity", TOAST_OPTS);
      })
      .finally(function () {
        setLoadingMore(false);
      });
  }

  var current = feed.candidateId === candidateId;
  var items = current ? feed.items : [];
  var groups = groupActivitiesByDay(items);
  var body;

  if (!current || feed.status === "loading") {
    body = (
      <Box sx={{ py: "32px", display: "flex", justifyContent: "center" }}>
        <CircularProgress size={24} sx={{ color: ADMIN_COLORS.brand }} />
      </Box>
    );
  } else if (feed.status === "error" && !items.length) {
    body = (
      <FeedMessage
        action={
          <Button variant="outlined" onClick={reload} sx={ADMIN_BUTTON_SX.outlined}>
            Try again
          </Button>
        }
      >
        Unable to load activity.
      </FeedMessage>
    );
  } else if (!items.length) {
    body = <FeedMessage>No activity to show yet.</FeedMessage>;
  } else {
    body = groups.map(function (group) {
      return (
        <Box component="section" key={group.day} aria-label={group.day}>
          <Typography component="h4" sx={Object.assign({ pt: "12px", pb: "2px" }, TEXT.day)}>
            {group.day}
          </Typography>
          <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {group.items.map(function (activity, index) {
              return (
                <ActivityItem
                  key={activity._id}
                  activity={activity}
                  last={index === group.items.length - 1}
                  onOpenForm={props.onOpenForm}
                />
              );
            })}
          </Box>
        </Box>
      );
    });
  }

  return (
    <Box>
      <Box aria-busy={feed.status === "loading" || feed.status === "refreshing"}>{body}</Box>

      {current && feed.hasMore ? (
        <Box sx={{ display: "flex", justifyContent: "center", pt: "12px" }}>
          <Button
            variant="outlined"
            onClick={loadOlder}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} color="inherit" /> : null}
            sx={[ADMIN_BUTTON_SX.outlined, { height: 36 }]}
          >
            {loadingMore ? "Loading…" : "Load older activity"}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

export default ActivityFeed;
